/**
 * Canonical RBAC source of truth for the WHOLE app — frontend + lambdas.
 *
 * This module is framework-agnostic (no React / AWS imports) so it can be the
 * single home for the role constants, `resolveProviderLevel`, and the role
 * predicates that both `frontend/src/utils/rbac.ts` and the infrastructure
 * lambdas re-export. Keep the level math here and nowhere else.
 *
 * ## AHP M4 taxonomy
 * Canonical write-path roles for service recipients / NoK are `Member` and
 * `Independent User`. Legacy Dynamo strings `Member User` / `Member Owner`
 * remain readable forever via the predicates below — do not hard-migrate rows
 * in the same change that starts writing `Member`.
 */

/**
 * The assignable role names (single source of truth). The three-way persona
 * classifier (provider staff / member / independent) is derived from these.
 */
export const ROLE_TYPES = {
  providerOwner: 'Provider Owner',
  providerAdmin: 'Provider Admin',
  providerBasic: 'Provider Basic',
  /** Canonical service-recipient role (AHP `user_type: member`). */
  member: 'Member',
  /** @deprecated legacy — still present on existing Users rows; do not write. */
  memberOwner: 'Member Owner',
  /** @deprecated legacy — still present on existing Users rows; do not write. */
  memberUser: 'Member User',
  independentUser: 'Independent User',
} as const

export type RoleTypeName = (typeof ROLE_TYPES)[keyof typeof ROLE_TYPES]

/** Back-compat named constants (existing imports across the app). */
export const ROLE_INDEPENDENT_USER = ROLE_TYPES.independentUser
export const ROLE_MEMBER_OWNER = ROLE_TYPES.memberOwner
export const ROLE_MEMBER_USER = ROLE_TYPES.memberUser
export const ROLE_MEMBER = ROLE_TYPES.member

/** Provider staff roles, highest → lowest privilege. */
export const PROVIDER_ROLES = [
  ROLE_TYPES.providerOwner,
  ROLE_TYPES.providerAdmin,
  ROLE_TYPES.providerBasic,
] as const

/**
 * Member (service recipient) roles offered in new invite / role pickers.
 * Legacy Owner/User remain readable via predicates but are not assignable.
 */
export const MEMBER_ROLES = [ROLE_TYPES.member] as const

/** Independent (unlinked) roles. */
export const INDEPENDENT_ROLES = [ROLE_TYPES.independentUser] as const

/** Every role string still accepted when reading / assigning via admin. */
export const ALL_ROLE_TYPES: readonly RoleTypeName[] = [
  ROLE_TYPES.providerOwner,
  ROLE_TYPES.providerAdmin,
  ROLE_TYPES.providerBasic,
  ROLE_TYPES.member,
  ROLE_TYPES.memberOwner,
  ROLE_TYPES.memberUser,
  ROLE_TYPES.independentUser,
]

export interface RoleProfile {
  roleType?: string | null
  providerLevel?: number | null
}

export function normalizeRoleType(roleType?: string | null): string {
  return (roleType ?? '').trim().toLowerCase()
}

export function isProvider(profile: RoleProfile): boolean {
  return normalizeRoleType(profile.roleType).includes('provider')
}

export function isMember(profile: RoleProfile): boolean {
  return normalizeRoleType(profile.roleType).includes('member')
}

export function isIndependentUser(profile: RoleProfile): boolean {
  return normalizeRoleType(profile.roleType).includes('independent')
}

export function isMemberOwner(profile: RoleProfile): boolean {
  const role = normalizeRoleType(profile.roleType)
  return role.includes('member') && role.includes('owner')
}

/**
 * Service-recipient member (not a Member Owner).
 * Matches legacy `Member User` and the canonical `Member` string.
 */
export function isMemberUserRole(profile: RoleProfile): boolean {
  const role = normalizeRoleType(profile.roleType)
  if (!role.includes('member')) return false
  if (role.includes('owner')) return false
  // Exact "member" OR legacy "member user" (contains "user").
  return role === 'member' || role.includes('user')
}

/**
 * Canonical provider-level resolver. An explicit `providerLevel` always wins;
 * otherwise the level is inferred from the role name. Member roles rank as
 * level 1 and are checked BEFORE the "owner" keyword because "Member Owner"
 * contains both "member" and "owner".
 */
export function resolveProviderLevel(profile: RoleProfile): number {
  if (profile.providerLevel != null) return profile.providerLevel
  const role = normalizeRoleType(profile.roleType)
  if (role.includes('member')) return 1
  if (role.includes('owner')) return 4
  if (role.includes('admin')) return 3
  if (role.includes('basic')) return 2
  return 1
}

/**
 * Provider Owner (L4) ONLY — the authoritative "can administer" gate for
 * server-side admin mutations (role changes, deactivation, member invites and
 * approvals). Excludes "Member Owner" (which also contains "owner"). This
 * mirrors the frontend `isProviderOwner` and the legacy `session-auth.isL4Admin`
 * (which allowed a level-4 super-admin regardless of the "provider" keyword).
 */
export function isL4ProviderOwner(profile: RoleProfile): boolean {
  if (isMember(profile) || isIndependentUser(profile)) return false
  const role = normalizeRoleType(profile.roleType)
  const level = resolveProviderLevel(profile)
  return level >= 4 || (role.includes('owner') && !role.includes('member'))
}

/** Independent users and service-recipient members use simplified nav. */
export function shouldUseSimplifiedNav(profile: RoleProfile): boolean {
  return isIndependentUser(profile) || isMemberUserRole(profile)
}

/** L3+ providers or legacy member owners may create member links. */
export function canCreateMemberLink(profile: RoleProfile): boolean {
  if (isMemberOwner(profile)) return true
  if (isMember(profile)) return false
  return resolveProviderLevel(profile) >= 3
}

/** Member users and independent users cannot create groups or announcements. */
export function canMemberLinkUserCreateGroup(profile: RoleProfile): boolean {
  if (isIndependentUser(profile) || isMemberUserRole(profile)) return false
  return true
}

/**
 * Display label for admin badges / pickers. Maps legacy stored strings onto the
 * M4 taxonomy without rewriting Dynamo rows.
 */
export function displayRoleLabel(roleType?: string | null): string {
  const raw = (roleType ?? '').trim()
  if (!raw) return ''
  if (raw === ROLE_TYPES.memberUser) return ROLE_TYPES.member
  if (raw === ROLE_TYPES.memberOwner) return 'Member (legacy owner)'
  return raw
}
